import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  MetricCard,
  PatientQueueList,
  DepartmentQueueManagement,
  MetricCardSkeleton,
  PatientQueueListSkeleton,
  DepartmentQueueSkeleton,
  Assignment
} from '@/domains/internal/dashboard/components';
import type { MetricCardProps } from '@/domains/internal/dashboard/components';
import { ConfirmationModal } from '@/shared/components/common';
import { dashboardService, DashboardMetrics, DepartmentQueuesResponse } from '@/domains/internal/dashboard/services/dashboardService';
import { logger } from '@/utils/logger';
import { useAuth } from '@/shared/contexts/AuthContext';
import { RootState } from '@/store/store';

const Dashboard: React.FC = () => {
  const { isAuthReady, isAuthenticated } = useAuth();
  const { selectedLocationId } = useSelector((state: RootState) => state.location);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    total_patients: 0,
    active_queue: 0,
    completed_tests: 0,
    pending_results: 0
  });
  const [assignmentList, setAssignmentList] = useState<Assignment[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [departmentQueuesLoading, setDepartmentQueuesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [departmentQueues, setDepartmentQueues] = useState<DepartmentQueuesResponse>({
    department_queues: [],
    total_departments: 0
  });
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [pendingPriorityChange, setPendingPriorityChange] = useState<{
    assignmentId: string;
    patientName: string;
    isPriority: boolean;
  } | null>(null);

  // Fetch live data from API with parallel loading for better performance
  useEffect(() => {
    // Wait for auth to be ready before fetching data
    if (!isAuthReady) {
      logger.debug('[Dashboard] Waiting for auth to be ready...');
      return;
    }

    // Only fetch if authenticated
    if (!isAuthenticated) {
      logger.debug('[Dashboard] User not authenticated, skipping data fetch');
      setMetricsLoading(false);
      setAssignmentsLoading(false);
      setDepartmentQueuesLoading(false);
      return;
    }

    const fetchAllData = async () => {
      try {
        logger.debug('[Dashboard] Auth ready and user authenticated, fetching dashboard data...');

        // Fetch all data in parallel for faster loading
        const [metricsData, assignmentsData, departmentQueuesData] = await Promise.all([
          dashboardService.getMetrics(selectedLocationId),
          dashboardService.getAssignments(selectedLocationId),
          dashboardService.getDepartmentQueues(selectedLocationId)
        ]);

        setMetrics(metricsData);
        setAssignmentList(assignmentsData.assignments);
        setDepartmentQueues(departmentQueuesData);
        logger.debug('[Dashboard] Dashboard data loaded successfully');
      } catch (err) {
        logger.error('[Dashboard] Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        // Set all loading states to false once all data is loaded
        setMetricsLoading(false);
        setAssignmentsLoading(false);
        setDepartmentQueuesLoading(false);
      }
    };

    fetchAllData();
  }, [isAuthReady, isAuthenticated, selectedLocationId]); // Re-fetch when location changes

  // Auto-select first department when data loads
  useEffect(() => {
    if (!selectedDepartment && departmentQueues.department_queues.length > 0) {
      setSelectedDepartment(departmentQueues.department_queues[0].department_code);
    }
  }, [departmentQueues, selectedDepartment]);

  const togglePriority = (assignmentId: string) => {
    const assignment = assignmentList.find(a => a.id === assignmentId);
    if (!assignment) return;

    setPendingPriorityChange({
      assignmentId,
      patientName: assignment.patient_name,
      isPriority: assignment.is_priority
    });
    setShowPriorityModal(true);
  };

  const handleConfirmPriorityChange = async () => {
    if (!pendingPriorityChange) return;

    try {
      const result = await dashboardService.updateAssignmentPriority(pendingPriorityChange.assignmentId);

      if (result.success) {
        setAssignmentList(prev =>
          prev.map(assignment =>
            assignment.id === pendingPriorityChange.assignmentId
              ? { ...assignment, is_priority: result.is_priority }
              : assignment
          )
        );
      }
    } catch (error) {
      logger.error('API priority update failed, using local update:', error);

      setAssignmentList(prev =>
        prev.map(assignment =>
          assignment.id === pendingPriorityChange.assignmentId
            ? { ...assignment, is_priority: !assignment.is_priority }
            : assignment
        )
      );
    }

    setShowPriorityModal(false);
    setPendingPriorityChange(null);
  };

  const handleCancelPriorityChange = () => {
    setShowPriorityModal(false);
    setPendingPriorityChange(null);
  };

  const metricsCards: MetricCardProps[] = [
    {
      title: 'Total Patients',
      value: metrics.total_patients,
      subtitle: "Today's registrations",
      iconType: 'users'
    },
    {
      title: 'Patients in Queue',
      value: metrics.active_queue,
      subtitle: "Currently waiting",
      iconType: 'clock'
    },
    {
      title: 'Completed Tests',
      value: metrics.completed_tests,
      subtitle: "Tests finished today",
      iconType: 'check'
    },
    {
      title: 'Pending Results',
      value: metrics.pending_results,
      subtitle: "Awaiting results",
      iconType: 'file'
    }
  ];

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {metricsLoading ? (
          <>
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
            <MetricCardSkeleton />
          </>
        ) : (
          metricsCards.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))
        )}
      </div>

      {/* Patients in Queue */}
      {assignmentsLoading ? (
        <PatientQueueListSkeleton />
      ) : (
        <PatientQueueList
          assignments={assignmentList}
          onTogglePriority={togglePriority}
        />
      )}

      {/* Department Queue Management */}
      {departmentQueuesLoading ? (
        <DepartmentQueueSkeleton />
      ) : (
        <DepartmentQueueManagement
          departmentQueues={departmentQueues}
          selectedDepartment={selectedDepartment}
          onSelectDepartment={setSelectedDepartment}
        />
      )}

      {/* Priority Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPriorityModal && !!pendingPriorityChange}
        title="Confirm Priority Change"
        message={
          pendingPriorityChange
            ? `Are you sure you want to ${
                pendingPriorityChange.isPriority ? 'remove priority from' : 'mark as priority'
              } ${pendingPriorityChange.patientName}?`
            : ''
        }
        confirmText={pendingPriorityChange?.isPriority ? 'Remove Priority' : 'Mark Priority'}
        isDestructive={true}
        onConfirm={handleConfirmPriorityChange}
        onClose={handleCancelPriorityChange}
      />
    </div>
  );
};

export default Dashboard;
